import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  IconButton
} from '@mui/material';
import DataTable from 'react-data-table-component';
import FilterListIcon from '@mui/icons-material/FilterList';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useDispatch, useSelector } from 'react-redux';
import { fetchStockReceivedReport, fetchUniqueBatches } from '../../redux/actions/stockIssue/stockIssueAction';
import { fetchEmployees } from '../../redux/actions/employee/employeeAction';
import { exportToCSV } from '../../utils/exportUtils';
import PageLoader from '../common/PageLoader';

const StockReceivedReportTable = () => {
  const dispatch = useDispatch();
  const { stockReceivedReport, loading, error, uniqueBatches } = useSelector(state => state.stockIssue);
  const { employees } = useSelector(state => state.employee);

  const DEFAULT_LIMIT = 200;

  const normalizeFilters = (input = {}) => {
    const hasDateRange = Boolean(input.date_from) || Boolean(input.date_to);
    return {
      ...input,
      today_only: hasDateRange ? false : true,
      limit: input.limit !== undefined ? input.limit : DEFAULT_LIMIT
    };
  };

  const [filters, setFilters] = useState({
    emp_code: '',
    batch_no: '',
    date_from: '',
    date_to: '',
    today_only: true,
    limit: DEFAULT_LIMIT
  });

  useEffect(() => {
    dispatch(fetchStockReceivedReport({ today_only: true, limit: DEFAULT_LIMIT }));
    dispatch(fetchEmployees());
    dispatch(fetchUniqueBatches('received'));
  }, [dispatch]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleApplyFilters = () => {
    const normalized = normalizeFilters(filters);
    setFilters(normalized);
    dispatch(fetchStockReceivedReport(normalized));
  };

  const handleClearFilters = () => {
    const cleared = {
      emp_code: '',
      batch_no: '',
      date_from: '',
      date_to: '',
      today_only: true,
      limit: DEFAULT_LIMIT
    };
    setFilters(cleared);
    dispatch(fetchStockReceivedReport(cleared));
  };

  const handleRefresh = () => {
    const normalized = normalizeFilters(filters);
    dispatch(fetchStockReceivedReport(normalized));
  };

  const handleExport = () => {
    const headers = [
      { key: 'is_processed_in_batch', label: 'Weight Processed' },
      { key: 'emp_code', label: 'Employee' },
      { key: 'batch_no', label: 'Batch No' },
      { key: 'stock_code', label: 'Stock Code' },
      { key: 'stock_type', label: 'Stock Type' },
      { key: 'qty', label: 'Quantity', type: 'numeric' },
      { key: 'weight', label: 'Weight', type: 'numeric' },
      { key: 'pure_weight', label: 'Pure Weight', type: 'numeric' },
      { key: 'received_by', label: 'Received By' },
      { key: 'received_at', label: 'Received At', type: 'date' },
      { key: 'created_by', label: 'Created By' },
      { key: 'created_at', label: 'Created At', type: 'date' }
    ];
    
    // Transform data to include processed status text
    const exportData = (stockReceivedReport?.details || []).map(item => ({
      ...item,
      is_processed_in_batch: item.is_processed_in_batch ? 'Processed' : 'Not Processed'
    }));
    
    exportToCSV(exportData, 'stock_received_report', headers);
  };

  const columns = [
    {
      name: 'Weight Processed',
      selector: row => row.is_processed_in_batch || 0,
      sortable: true,
      width: '140px',
      cell: row => (
        <span style={{
          display: 'inline-block',
          padding: '4px 8px',
          borderRadius: '4px',
          fontSize: '0.75rem',
          fontWeight: 500,
          backgroundColor: row.is_processed_in_batch ? '#4caf50' : '#ff9800',
          color: 'white',
          textAlign: 'center',
          minWidth: '80px'
        }}>
          {row.is_processed_in_batch ? 'Processed' : 'Not Processed'}
        </span>
      )
    },
    {
      name: 'Employee',
      selector: row => row.emp_code || 'N/A',
      sortable: true,
      width: '180px'
    },
    {
      name: 'Batch No',
      selector: row => row.batch_no || 'N/A',
      sortable: true,
      width: '140px'
    },
    {
      name: 'Stock Code',
      selector: row => row.stock_code || 'N/A',
      sortable: true,
      width: '140px'
    },
    {
      name: 'Stock Type',
      selector: row => row.stock_type || 'N/A',
      sortable: true,
      width: '120px',
      cell: row => (
        <Box
          sx={{
            display: 'inline-block',
            padding: '4px 8px',
            borderRadius: '12px',
            fontSize: '0.75rem',
            fontWeight: 500,
            backgroundColor: row.stock_type === 'Gold' ? '#ff9800' : '#1976d2',
            color: 'white',
            textAlign: 'center',
            minWidth: '60px'
          }}
        >
          {row.stock_type || 'N/A'}
        </Box>
      )
    },
    {
      name: 'Quantity',
      selector: row => parseInt(row.qty) || 0,
      sortable: true,
      width: '120px',
      right: true
    },
    {
      name: 'Weight',
      selector: row => parseFloat(row.weight || 0).toFixed(3),
      sortable: true,
      width: '120px',
      right: true
    },
    {
      name: 'Pure Weight',
      selector: row => parseFloat(row.pure_weight || 0).toFixed(3),
      sortable: true,
      width: '140px',
      right: true
    },
    {
      name: 'Received By',
      selector: row => row.received_by || 'N/A',
      sortable: true,
      width: '140px'
    },
    {
      name: 'Received At',
      selector: row => row.received_at || 'N/A',
      sortable: true,
      width: '180px'
    },
    {
      name: 'Created By',
      selector: row => row.created_by || 'N/A',
      sortable: true,
      width: '140px'
    },
    {
      name: 'Created At',
      selector: row => row.created_at || 'N/A',
      sortable: true,
      width: '180px'
    }
  ];

  const summaryColumns = [
    {
      name: 'Weight Processed',
      selector: row => row.is_processed_in_batch || 0,
      sortable: true,
      width: '140px',
      cell: row => (
        <span style={{
          display: 'inline-block',
          padding: '4px 8px',
          borderRadius: '4px',
          fontSize: '0.75rem',
          fontWeight: 500,
          backgroundColor: row.is_processed_in_batch ? '#4caf50' : '#ff9800',
          color: 'white',
          textAlign: 'center',
          minWidth: '80px'
        }}>
          {row.is_processed_in_batch ? 'Processed' : 'Not Processed'}
        </span>
      )
    },
    {
      name: 'Employee',
      selector: row => row.emp_code || 'N/A',
      sortable: true,
      width: '180px'
    },
    {
      name: 'Batch No',
      selector: row => row.batch_no || 'N/A',
      sortable: true,
      width: '140px'
    },
    {
      name: 'Total Quantity',
      selector: row => parseInt(row.total_qty) || 0,
      sortable: true,
      width: '140px',
      right: true
    },
    {
      name: 'Total Weight',
      selector: row => parseFloat(row.total_weight || 0).toFixed(3),
      sortable: true,
      width: '140px',
      right: true
    },
    {
      name: 'Transaction Count',
      selector: row => parseInt(row.transaction_count) || 0,
      sortable: true,
      width: '140px',
      right: true
    },
    {
      name: 'Last Received Date',
      selector: row => row.last_received_date || 'N/A',
      sortable: true,
      width: '180px'
    }
  ];

  if (loading) {
    return (
      <>
        <PageLoader loading />
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Error loading stock received report: {error}
      </Alert>
    );
  }

  return (
    <Box>
      <PageLoader loading={loading} />
      <Typography variant="h5" gutterBottom>
        Stock Received Report
      </Typography>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small" sx={{ minWidth: 250 }}>
              <InputLabel>Employee</InputLabel>
              <Select
                value={filters.emp_code}
                onChange={(e) => handleFilterChange('emp_code', e.target.value)}
                label="Employee"
              >
                <MenuItem value="">All Employees</MenuItem>
                {employees.map(emp => (
                  <MenuItem key={emp.id} value={emp.emp_code}>
                    {emp.emp_code} - {emp.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Batch No</InputLabel>
              <Select
                value={filters.batch_no}
                onChange={(e) => handleFilterChange('batch_no', e.target.value)}
                label="Batch No"
              >
                <MenuItem value="">All Batches</MenuItem>
                {uniqueBatches.map(batch => (
                  <MenuItem key={batch.batch_no} value={batch.batch_no}>
                    {batch.batch_no}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Date From"
              type="date"
              value={filters.date_from}
              onChange={(e) => handleFilterChange('date_from', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Date To"
              type="date"
              value={filters.date_to}
              onChange={(e) => handleFilterChange('date_to', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Box display="flex" gap={1}>
              <Button
                variant="contained"
                startIcon={<FilterListIcon />}
                onClick={handleApplyFilters}
                size="small"
              >
                Apply
              </Button>
              <Button
                variant="outlined"
                onClick={handleClearFilters}
                size="small"
              >
                Clear
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  const recent = {
                    emp_code: '',
                    batch_no: '',
                    date_from: '',
                    date_to: '',
                    today_only: true,
                    limit: DEFAULT_LIMIT
                  };
                  setFilters(recent);
                  dispatch(fetchStockReceivedReport(recent));
                }}
                size="small"
              >
                Load Recent
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  const allFilters = normalizeFilters({
                    ...filters,
                    today_only: false,
                    limit: 0
                  });
                  setFilters(allFilters);
                  dispatch(fetchStockReceivedReport(allFilters));
                }}
                size="small"
              >
                Load All
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Summary Table */}
      <Paper sx={{ mb: 2 }}>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6">
            Summary by Employee and Batch
          </Typography>
        </Box>
        <DataTable
          columns={summaryColumns}
          data={stockReceivedReport?.summary || []}
          pagination
          paginationPerPage={10}
          paginationRowsPerPageOptions={[10, 25, 50]}
          highlightOnHover
          pointerOnHover
          responsive
          noDataComponent="No summary data available"
        />
      </Paper>

      {/* Detailed Table */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <Button variant="outlined" onClick={handleExport} size="small">Export CSV</Button>
      </Box>
      <Paper>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            Detailed Stock Received Data
          </Typography>
          <Tooltip title="Refresh">
            <IconButton onClick={handleRefresh}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <DataTable
          columns={columns}
          data={stockReceivedReport?.details || []}
          pagination
          paginationPerPage={10}
          paginationRowsPerPageOptions={[10, 25, 50]}
          highlightOnHover
          pointerOnHover
          responsive
          noDataComponent="No detailed data available"
        />
      </Paper>
    </Box>
  );
};

export default StockReceivedReportTable; 